<div class="job-detail">
  <app-breadcrumb [levels]="breadcrumbLevels()"></app-breadcrumb>

  @if (errorMessage()) {
    <div class="job-detail__state job-detail__state--error" role="status">
      <p class="job-detail__state-text">{{ errorMessage() }}</p>
      <button type="button" class="job-detail__back-link" (click)="navigateBack()">
        Back to Translation Manager
      </button>
    </div>
  } @else if (isLoading()) {
    <div class="job-detail__state job-detail__state--loading" role="status">
      <p class="job-detail__state-text">Loading job details...</p>
    </div>
  } @else if (jobDetail(); as detail) {
    <div class="job-detail__content">
      <!-- Header -->
      <header class="job-detail__header">
        <!-- Top row: Status pill and Cancel link -->
        <div class="job-detail__header-top">
          <span [class]="'status-pill status-pill--' + detail.statusKey">
            @if (detail.statusIcon) {
              <mat-icon class="status-pill__icon" aria-hidden="true">{{ detail.statusIcon }}</mat-icon>
            }
            <span class="status-pill__label">{{ detail.statusLabel }}</span>
          </span>
          <button
            type="button"
            class="job-detail__cancel-link"
            (click)="navigateBack()"
            aria-label="Cancel Job"
          >
            Cancel Job
          </button>
        </div>
        <!-- Bottom row: Title and Meta -->
        <div class="job-detail__header-bottom">
          <h2 class="job-detail__title">{{ detail.jobName }}</h2>
          <div class="job-detail__meta">
            <span class="job-detail__meta-item">
              <span class="job-detail__meta-label">Job ID:</span> {{ detail.jobUid }}
            </span>
            <span class="job-detail__meta-item">
              <span class="job-detail__meta-label">Request Date:</span> {{ detail.requestDate }}
            </span>
            <span class="job-detail__meta-item">
              <span class="job-detail__meta-label">Target Date:</span> {{ detail.targetDate }}
            </span>
          </div>
        </div>
      </header>

      <!-- Tabs -->
      <nav
        #tabsNav
        class="job-detail__tabs"
        role="tablist"
        aria-label="Job information tabs"
        (keydown)="onTabsKeydown($event)"
      >
        @for (tab of tabs(); track tab.id; let index = $index) {
          <button
            #tabButton
            type="button"
            role="tab"
            class="job-detail__tab"
            [class.is-active]="activeTab() === tab.id"
            [id]="getTabId(tab.id)"
            [attr.aria-selected]="activeTab() === tab.id"
            [attr.aria-controls]="getTabAriaControls(tab.id)"
            [attr.tabindex]="activeTab() === tab.id ? 0 : -1"
            (click)="setActiveTab(tab.id)"
          >
            {{ tab.label }}
            @if (tab.id === 'quotes' && notificationService.isAwaitingVendorJob(jobDetail()?.jobUid ?? '')) {
              <span
                class="job-detail__tab-badge"
                aria-label="New quote notifications"
              ></span>
            }
            @if (tab.id === 'translated-files' && notificationService.isCompletedDocsJob(jobDetail()?.jobUid ?? '')) {
              <span
                class="job-detail__tab-badge"
                aria-label="New translated files notifications"
              ></span>
            }
          </button>
        }
        <span
          class="job-detail__tab-indicator"
          aria-hidden="true"
          [style.transform]="tabIndicatorTransform()"
          [style.width.px]="tabIndicatorWidth()"
          [style.opacity]="tabIndicatorWidth() > 0 ? 1 : 0"
        ></span>
      </nav>

      <div
        class="job-detail__tab-content"
        role="tabpanel"
        [id]="getTabAriaControls(renderedTab())"
        [attr.aria-labelledby]="getTabId(renderedTab())"
        [attr.aria-label]="getRenderedTabPanelLabel()"
        [class.job-detail__tab-content--exiting]="tabContentState() === 'exiting'"
        [class.job-detail__tab-content--entering]="tabContentState() === 'entering'"
      >
        @if (renderedTab() === 'details') {
          <!-- Summary Section -->
          <section class="job-detail__section">
            <h4 class="job-detail__section-title">Summary</h4>
            <div class="job-detail__summary-grid">
              <div class="job-detail__summary-item">
                <span class="job-detail__summary-label">Content Type:</span>
                <span class="job-detail__summary-value job-detail__summary-value">{{ detail.contentType | titlecase }}</span>
              </div>
              <div class="job-detail__summary-item">
                <span class="job-detail__summary-label">Therapeutic Area:</span>
                <span class="job-detail__summary-value">{{ detail.therapeuticArea }}</span>
              </div>
              <div class="job-detail__summary-item">
                <span class="job-detail__summary-label">Translation Certificate:</span>
                <span class="job-detail__summary-value job-detail__summary-value">{{ detail.translationCertificate }}</span>
              </div>
              <div class="job-detail__summary-item">
                <span class="job-detail__summary-label">Cost Centre Code:</span>
                <span class="job-detail__summary-value">{{ detail.costCentre }}</span>
              </div>
              <div class="job-detail__summary-item">
                <span class="job-detail__summary-label">Target Audience:</span>
                <span class="job-detail__summary-value">{{ detail.targetAudience }}</span>
              </div>
              <div class="job-detail__summary-item">
                <span class="job-detail__summary-label">Document Tone of Voice:</span>
                <span class="job-detail__summary-value job-detail__summary-value">{{ detail.documentTone }}</span>
              </div>
            </div>
          </section>

          <!-- Languages Section -->
          <section class="job-detail__section">
            <h4 class="job-detail__section-title">Languages</h4>
            <div class="job-detail__languages">
              <div class="job-detail__language-row">
                <span class="job-detail__language-label">Source Language:</span>
                <span class="language-chip language-chip--source">{{ detail.sourceLanguage }}</span>
              </div>
              <div class="job-detail__language-row">
                <span class="job-detail__language-label">Target Language(s):</span>
                <div class="job-detail__language-chips">
                  @for (lang of detail.targetLanguages; track lang) {
                    <span class="language-chip language-chip--target">{{ lang }}</span>
                  }
                  @if (detail.targetLanguages.length > 0) {
                    <span class="job-detail__language-count">({{ detail.targetLanguages.length }} Total)</span>
                  }
                </div>
              </div>
            </div>
          </section>

          <!-- Additional Information Section -->
          @if (detail.additionalInfo) {
            <section class="job-detail__section">
              <h4 class="job-detail__section-title">Additional Information for Vendors</h4>
              <p class="job-detail__additional-info">{{ detail.additionalInfo }}</p>
            </section>
          }

          <!-- Source Files Section -->
          <section class="job-detail__section job-detail__section--source-files">
            <h4 class="job-detail__section-title">Source Files</h4>
            <p class="job-detail__dtp-requirement">
              <span class="job-detail__dtp-requirement-label">Desktop Publishing Requirements:</span>
              <span class="job-detail__dtp-requirement-value">{{ detail.desktopPublishingRequirement }}</span>
            </p>

            <div class="job-detail__files-grid">
              <!-- Basic Format Translation -->
              <div class="job-detail__file-group">
                <h5 class="job-detail__file-group-title">
                  Basic Format Translation ({{ detail.basicFormatFiles.length }})
                </h5>
                @if (isFilesLoading()) {
                  <div class="job-detail__files-loading" role="status" aria-live="polite">
                    <app-spinner
                      [display]="true"
                      [class]="'job-detail__files-loading-spinner'"
                      [diameter]="16"
                    ></app-spinner>
                    <span class="job-detail__files-loading-text">Loading files...</span>
                  </div>
                } @else if (detail.basicFormatFiles.length > 0) {
                  <table class="job-detail__file-table" aria-label="Basic format translation files">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          class="job-detail__file-table-header job-detail__file-table-header--name"
                          [attr.aria-sort]="getFileAriaSort('basic', 'name')"
                        >
                          <button
                            type="button"
                            class="job-detail__header-button"
                            (click)="toggleFileSort('basic', 'name')"
                            aria-label="Sort basic format files by file name"
                          >
                            <span>File Name</span>
                            <mat-icon class="job-detail__sort-icon" aria-hidden="true">swap_vert</mat-icon>
                          </button>
                        </th>
                        <th
                          scope="col"
                          class="job-detail__file-table-header job-detail__file-table-header--size"
                          [attr.aria-sort]="getFileAriaSort('basic', 'size')"
                        >
                          <button
                            type="button"
                            class="job-detail__header-button"
                            (click)="toggleFileSort('basic', 'size')"
                            aria-label="Sort basic format files by size"
                          >
                            <span>Size</span>
                            <mat-icon class="job-detail__sort-icon" aria-hidden="true">swap_vert</mat-icon>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (file of detail.basicFormatFiles; track file.name) {
                        <tr>
                          <td class="job-detail__file-name-cell">{{ file.name }}</td>
                          <td class="job-detail__file-size-cell">{{ file.size }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="job-detail__no-files">No files available.</p>
                }
              </div>

              <!-- Desktop Publishing Translation -->
              <div class="job-detail__file-group">
                <h5 class="job-detail__file-group-title">
                  Desktop Publishing Translation ({{ detail.dtpFiles.length }})
                </h5>
                @if (isFilesLoading()) {
                  <div class="job-detail__files-loading" role="status" aria-live="polite">
                    <app-spinner
                      [display]="true"
                      [class]="'job-detail__files-loading-spinner'"
                      [diameter]="16"
                    ></app-spinner>
                    <span class="job-detail__files-loading-text">Loading files...</span>
                  </div>
                } @else if (detail.dtpFiles.length > 0) {
                  <table class="job-detail__file-table" aria-label="Desktop publishing translation files">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          class="job-detail__file-table-header job-detail__file-table-header--name"
                          [attr.aria-sort]="getFileAriaSort('dtp', 'name')"
                        >
                          <button
                            type="button"
                            class="job-detail__header-button"
                            (click)="toggleFileSort('dtp', 'name')"
                            aria-label="Sort desktop publishing files by file name"
                          >
                            <span>File Name</span>
                            <mat-icon class="job-detail__sort-icon" aria-hidden="true">swap_vert</mat-icon>
                          </button>
                        </th>
                        <th
                          scope="col"
                          class="job-detail__file-table-header job-detail__file-table-header--size"
                          [attr.aria-sort]="getFileAriaSort('dtp', 'size')"
                        >
                          <button
                            type="button"
                            class="job-detail__header-button"
                            (click)="toggleFileSort('dtp', 'size')"
                            aria-label="Sort desktop publishing files by size"
                          >
                            <span>Size</span>
                            <mat-icon class="job-detail__sort-icon" aria-hidden="true">swap_vert</mat-icon>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (file of detail.dtpFiles; track file.name) {
                        <tr>
                          <td class="job-detail__file-name-cell">{{ file.name }}</td>
                          <td class="job-detail__file-size-cell">{{ file.size }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="job-detail__no-files">No files available.</p>
                }
              </div>
            </div>
          </section>

          <!-- Reference Files Section -->
          <section class="job-detail__section">
            <h4 class="job-detail__section-title">Reference Files</h4>
            <div class="job-detail__reference-box">
              @if (isFilesLoading()) {
                <div class="job-detail__files-loading" role="status" aria-live="polite">
                  <app-spinner
                    [display]="true"
                    [class]="'job-detail__files-loading-spinner'"
                    [diameter]="16"
                  ></app-spinner>
                  <span class="job-detail__files-loading-text">Loading files...</span>
                </div>
              } @else if (detail.referenceFiles.length > 0) {
                <table class="job-detail__file-table" aria-label="Reference files">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        class="job-detail__file-table-header job-detail__file-table-header--name"
                        [attr.aria-sort]="getFileAriaSort('reference', 'name')"
                      >
                        <button
                          type="button"
                          class="job-detail__header-button"
                          (click)="toggleFileSort('reference', 'name')"
                          aria-label="Sort reference files by file name"
                        >
                          <span>File Name</span>
                          <mat-icon class="job-detail__sort-icon" aria-hidden="true">swap_vert</mat-icon>
                        </button>
                      </th>
                      <th
                        scope="col"
                        class="job-detail__file-table-header job-detail__file-table-header--size"
                        [attr.aria-sort]="getFileAriaSort('reference', 'size')"
                      >
                        <button
                          type="button"
                          class="job-detail__header-button"
                          (click)="toggleFileSort('reference', 'size')"
                          aria-label="Sort reference files by size"
                        >
                          <span>Size</span>
                          <mat-icon class="job-detail__sort-icon" aria-hidden="true">swap_vert</mat-icon>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (file of detail.referenceFiles; track file.name) {
                      <tr>
                        <td class="job-detail__file-name-cell">{{ file.name }}</td>
                        <td class="job-detail__file-size-cell">{{ file.size }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              } @else {
                <p class="job-detail__no-files">No reference files available.</p>
              }
            </div>
          </section>
      } @else if (renderedTab() === 'quotes') {
          <app-quotes
            [order]="fullOrder() ?? undefined"
            (quoteConfirmed)="onQuoteConfirmed($event)"
          />
      } @else if (renderedTab() === 'translated-files') {
          <app-translated-files />
      }
      </div>
   </div>
  }
</div>
